import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@material-ui/core";
import { useDropzone } from "react-dropzone";
import { DropzoneContainer, ExistingMaterialDropzoneContainer } from "./Dropzone.styled";
import { useAlert } from "hooks";

interface DropzoneProps {
    disabled?: boolean;
    multiple?: boolean;
    allowed?: boolean;
    sizeX?: number;
    sizeY?: number;
    spacing?: number;
    showDimensions?: boolean;
    label?: string;
    noClick?: boolean;
    mimeTypes?: string | string[];
    onDrop: <T extends File>(acceptedFiles: T[]) => void;
}

export const Dropzone: React.FC<DropzoneProps> = ({
    disabled = false,
    multiple = true,
    allowed = true,
    label,
    sizeX = 0,
    sizeY = 0,
    spacing = 0,
    showDimensions = true,
    noClick = false,
    mimeTypes,
    onDrop,
    children,
}) => {
    const { t } = useTranslation();
    const { toggleAlert } = useAlert();
    const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
        multiple,
        disabled,
        noClick,
        accept: mimeTypes,
        onDrop: (files, fileRejections) => {
            if (fileRejections.length) {
                // take first file and first error
                const [file] = fileRejections;
                const [error] = file.errors;

                switch (error.code) {
                    case "too-many-files":
                        return toggleAlert({
                            message: t("campaign.errors.uploadMultipleFilesError"),
                            type: "error",
                        });

                    case "file-invalid-type":
                        return toggleAlert({
                            message: t("campaign.errors.uploadFileExtension"),
                            type: "error",
                        });

                    default:
                        return toggleAlert({
                            message: error.message,
                            type: "error",
                        });
                }
            }

            onDrop(files);
        },
    });
    const rootProps = getRootProps({
        isDragActive,
        isDragAccept,
        isDragReject,
        isDisabled: disabled,
        isDisallowed: !allowed,
        spacing,
        sizeX,
        sizeY,
    });

    if (children) {
        return (
            <ExistingMaterialDropzoneContainer {...rootProps}>
                <input {...getInputProps()} />
                {children}
            </ExistingMaterialDropzoneContainer>
        );
    }

    return (
        <DropzoneContainer {...rootProps}>
            {!allowed ? (
                <Typography variant="body1">Uplaod is not not possible</Typography>
            ) : (
                <>
                    <input {...getInputProps()} />
                    <Typography component="div" variant="body1">
                        <Box p={1} textAlign="center">
                            {label}
                        </Box>
                    </Typography>
                    {sizeX !== 0 && sizeY !== 0 && showDimensions && (
                        <Typography variant="body1">
                            {sizeX} x {sizeY} px
                        </Typography>
                    )}
                </>
            )}
        </DropzoneContainer>
    );
};
